import { Avatar as AvatarPrimitive } from "radix-ui";
import type { HTMLAttributes, ReactNode } from "react";
import { cn, hueOf, initials } from "@/lib/utils";

// ------------------------------------------------------------------ Avatar
export function Avatar({ src, name, size = 32, ring, className, badge }: { src?: string | null; name: string; size?: number; ring?: boolean; className?: string; badge?: ReactNode }) {
  const hue = hueOf(name);
  return (
    <span className={cn("relative inline-flex shrink-0", className)} style={{ width: size, height: size }}>
      <AvatarPrimitive.Root className={cn("inline-flex size-full overflow-hidden rounded-full", ring && "ring-2 ring-surface")}>
        {src && <AvatarPrimitive.Image src={src} alt="" className="size-full object-cover" />}
        <AvatarPrimitive.Fallback
          delayMs={src ? 300 : 0}
          className="grid size-full place-items-center font-semibold text-white"
          style={{ background: `linear-gradient(135deg, hsl(${hue} 70% 58%), hsl(${(hue + 40) % 360} 70% 45%))`, fontSize: Math.max(10, size * 0.38) }}
          aria-hidden
        >
          {initials(name)}
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>
      {badge && <span className="absolute -bottom-1 -end-1">{badge}</span>}
    </span>
  );
}

export function AvatarStack({ people, size = 24, max = 3 }: { people: { name: string; src?: string | null }[]; size?: number; max?: number }) {
  const shown = people.slice(0, max);
  return (
    <span className="inline-flex items-center">
      {shown.map((p, i) => (
        <span key={i} className={i ? "-ms-2" : ""}>
          <Avatar name={p.name} src={p.src} size={size} ring />
        </span>
      ))}
      {people.length > max && <span className="-ms-2 grid place-items-center rounded-full bg-surface-3 text-[10px] font-semibold text-ink-2 ring-2 ring-surface" style={{ width: size, height: size }}>+{people.length - max}</span>}
    </span>
  );
}

// ------------------------------------------------------------------ Skeleton
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("skeleton", className)} aria-hidden {...props} />;
}

// ------------------------------------------------------------------ Progress
export function Progress({ value, className, tone = "accent", label }: { value: number; className?: string; tone?: "accent" | "ok" | "warn" | "bad" | "brand"; label?: string }) {
  const bar = { accent: "bg-accent", ok: "bg-ok", warn: "bg-warn", bad: "bg-bad", brand: "brand-bg" }[tone];
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-surface-3", className)} role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className={cn("h-full rounded-full transition-[width] duration-500 ease-out", bar)} style={{ width: `${v}%` }} />
    </div>
  );
}

// ------------------------------------------------------------------ Kbd
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cn("inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-line border-b-2 bg-surface px-1 font-sans text-[11px] font-medium text-ink-2", className)}>{children}</kbd>;
}

// ------------------------------------------------------------------ Num: numbers and measurements read LTR inside Arabic
export function Num({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span dir="ltr" className={cn("num inline-block", className)}>
      {children}
    </span>
  );
}

export function Separator({ className, vertical }: { className?: string; vertical?: boolean }) {
  return <div role="separator" aria-orientation={vertical ? "vertical" : "horizontal"} className={cn(vertical ? "w-px self-stretch bg-line" : "h-px w-full bg-line", className)} />;
}

/** Visually hidden, read by screen readers. */
export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}
